import { DisplayObject, RenderKind } from './DisplayObject';
import { Graphics } from './Graphics';
import { TRect, invalidRect, rectUnion, boundsToTwips } from '../utils/numeric';
import type { MorphShapeDef, ShapeDef, ImageRef } from '../../assets/manifest';
import type { ImageForRender, SymbolContext } from '../symbols/types';

/** Alpha test of a rasterized ImageRef at a local pixel point. `undefined` = no pixel data. */
export function imageAlphaAt(sym: SymbolContext | null, ref: ImageRef | null, lx: number, ly: number): boolean | undefined {
  if (!sym || !ref) return undefined;
  const img = sym.image(ref.src);
  if (!img) return undefined;
  const scale = ref.scale || 1;
  const sx = ref.sx ?? 0;
  const sy = ref.sy ?? 0;
  const sw = ref.sw ?? img.width;
  const sh = ref.sh ?? img.height;
  const ix = Math.floor((lx - ref.offsetX) * scale);
  const iy = Math.floor((ly - ref.offsetY) * scale);
  if (ix < 0 || iy < 0 || ix >= sw || iy >= sh) return false;
  const px = sx + ix;
  const py = sy + iy;
  if (px < 0 || py < 0 || px >= img.width || py >= img.height) return false;
  return img.data[(py * img.width + px) * 4 + 3] > 0;
}

/**
 * flash.display.Shape. Timeline shapes carry a manifest ShapeDef (`$def`: bounds + rasterized
 * ImageRef); script shapes draw through `graphics`.
 */
export class Shape extends DisplayObject {
  /** @internal */ $graphics: Graphics | null;
  /** @internal */ $def: ShapeDef | null;

  constructor() {
    super();
    this.$graphics = null;
    this.$def = null;
  }

  get graphics(): Graphics {
    return this.$graphics || (this.$graphics = new Graphics(this));
  }

  /** @internal */ get $renderKind(): RenderKind {
    return 'shape';
  }

  $selfBounds(): TRect {
    let r = this.$def ? boundsToTwips(this.$def.bounds) : invalidRect();
    if (this.$graphics) r = rectUnion(r, this.$graphics.$getBounds());
    return r;
  }

  $hitTestLocalContent(gx: number, gy: number): boolean {
    const p = this.$globalToLocalPx(gx, gy);
    if (!p) return false;
    const [lx, ly] = p;
    if (this.$graphics && this.$graphics.$hitTest(lx, ly)) return true;
    const def = this.$def;
    if (!def) return false;
    const b = def.bounds;
    if (lx < b.xMin || lx > b.xMax || ly < b.yMin || ly > b.yMax) return false;
    const a = imageAlphaAt(this.$sym, def.image, lx, ly);
    return a === undefined ? true : a;
  }

  $replaceCharacter(charId: number): void {
    const def = this.$sym?.character(charId);
    if (def && def.type === 'shape') {
      this.$def = def;
      this.$charId = charId;
      this.$invalidate();
    }
  }

  /** Renderer seam: rasterized image of the timeline shape (if any). */
  $imageForRender(): ImageForRender | null {
    const def = this.$def;
    if (!def || !def.image || !this.$sym) return null;
    const hd = def.image.hd;
    return {
      ref: def.image,
      url: this.$sym.resolve(def.image.src),
      image: this.$sym.image(def.image.src),
      ...(hd ? { hdUrl: this.$sym.resolve(hd.src), hdImage: this.$sym.image(hd.src) } : {}),
    };
  }
}

/** Timeline morph shape (flash.display.MorphShape): rendered from the sample nearest to `ratio`. */
export class MorphShape extends DisplayObject {
  /** @internal */ $def: MorphShapeDef | null;

  constructor() {
    super();
    this.$def = null;
  }

  /** @internal */ get $renderKind(): RenderKind {
    return 'morph';
  }

  $setRatio(r: number): void {
    if (this.$ratio === r) return;
    this.$ratio = r;
    this.$invalidate();
  }

  /** Current sample (nearest ratio). */
  $sample(): MorphShapeDef['samples'][number] | null {
    const def = this.$def;
    if (!def || !def.samples.length) return null;
    let best = def.samples[0];
    for (const s of def.samples) if (Math.abs(s.ratio - this.$ratio) < Math.abs(best.ratio - this.$ratio)) best = s;
    return best;
  }

  $selfBounds(): TRect {
    const def = this.$def;
    if (!def) return invalidRect();
    const t = this.$ratio / 65535;
    const a = def.startBounds;
    const b = def.endBounds;
    return boundsToTwips({
      xMin: a.xMin + (b.xMin - a.xMin) * t,
      yMin: a.yMin + (b.yMin - a.yMin) * t,
      xMax: a.xMax + (b.xMax - a.xMax) * t,
      yMax: a.yMax + (b.yMax - a.yMax) * t,
    });
  }

  $hitTestLocalContent(gx: number, gy: number): boolean {
    const s = this.$sample();
    if (!s) return true;
    const p = this.$globalToLocalPx(gx, gy);
    if (!p) return false;
    const a = imageAlphaAt(this.$sym, s.image, p[0], p[1]);
    return a === undefined ? true : a;
  }

  $replaceCharacter(charId: number): void {
    const def = this.$sym?.character(charId);
    if (def && def.type === 'morph') {
      this.$def = def;
      this.$charId = charId;
      this.$invalidate();
    }
  }

  $imageForRender(): ImageForRender | null {
    const s = this.$sample();
    if (!s || !s.image || !this.$sym) return null;
    return { ref: s.image, url: this.$sym.resolve(s.image.src), image: this.$sym.image(s.image.src) };
  }
}
